"use client";

import dynamic from "next/dynamic";
import { useEffect, useMemo, useRef, useState } from "react";
import { playSound } from "@/lib/game/audio";
import { pickPuzzle, puzzleGridSize } from "@/lib/game/puzzles";
import { formatStarReward } from "@/lib/game/stars";
import { ActivityLayout } from "./ActivityLayout";
import { ActivitySuccessOverlay } from "./ActivitySuccessOverlay";
import { DifficultyPicker } from "./DifficultyPicker";
import type { PuzzleSceneHandle } from "./PixiPuzzle";
import { GameIconButton } from "../game-ui";
import { useGame } from "../GameProvider";

const PixiPuzzle = dynamic(
  () => import("./PixiPuzzle").then((m) => m.PixiPuzzle),
  {
    ssr: false,
    loading: () => (
      <div className="flex h-full items-center justify-center bg-indigo-950 text-sm text-violet-200">
        Loading puzzle…
      </div>
    ),
  },
);

function PuzzleGame({
  challengeKey,
  onPlayAgain,
}: {
  challengeKey: number;
  onPlayAgain: () => void;
}) {
  const { completeActivity, progress } = useGame();
  const sceneRef = useRef<PuzzleSceneHandle | null>(null);
  const activityId = "space-puzzle";
  const difficulty = progress.difficulty;
  const completedRef = useRef(false);

  const puzzle = useMemo(
    () => pickPuzzle(difficulty, challengeKey),
    [difficulty, challengeKey],
  );
  const { cols, rows } = useMemo(() => puzzleGridSize(difficulty), [difficulty]);
  const totalPieces = cols * rows;

  const [placed, setPlaced] = useState(0);
  const [feedback, setFeedback] = useState<"idle" | "right">("idle");

  useEffect(() => {
    setPlaced(0);
    setFeedback("idle");
    completedRef.current = false;
    return () => {
      sceneRef.current = null;
    };
  }, [puzzle.id, puzzle.seed, cols, rows]);

  const handleComplete = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    sceneRef.current?.setSolutionPreview(false);
    setFeedback("right");
    playSound("correct");
    completeActivity(activityId, difficulty);
  };

  const showPreview = (show: boolean) => {
    if (feedback === "right") return;
    sceneRef.current?.setSolutionPreview(show);
  };

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-gradient-to-b from-indigo-950 to-slate-900 text-white">
      <p className="shrink-0 py-2 text-center text-lg font-extrabold text-violet-200">
        {puzzle.name}
      </p>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <PixiPuzzle
          puzzle={puzzle}
          cols={cols}
          rows={rows}
          onSceneReady={(handle) => {
            sceneRef.current = handle;
          }}
          onProgress={(p) => setPlaced(p)}
          onComplete={handleComplete}
        />

        {feedback === "idle" && (
          <div className="pointer-events-none absolute bottom-28 right-3 z-20">
            <GameIconButton
              icon="preview"
              tone="amber"
              size="md"
              className="pointer-events-auto"
              label="Hold to peek at solution"
              onPointerDown={() => showPreview(true)}
              onPointerUp={() => showPreview(false)}
              onPointerLeave={() => showPreview(false)}
              onPointerCancel={() => showPreview(false)}
            />
          </div>
        )}
      </div>

      <p className="shrink-0 border-t border-violet-500/30 bg-slate-900/80 py-2 text-center text-xs font-semibold text-violet-200">
        {placed} / {totalPieces} correct
      </p>

      <ActivitySuccessOverlay
        show={feedback === "right"}
        title="Puzzle complete!"
        subtitle={puzzle.name}
        theme="indigo"
        rewardLabel={formatStarReward(difficulty)}
        onPlayAgain={onPlayAgain}
      />
    </div>
  );
}

export function PuzzleActivity() {
  const { progress, setDifficulty, trackActivitySession } = useGame();
  const [challengeKey, setChallengeKey] = useState(0);

  useEffect(() => {
    trackActivitySession("space-puzzle");
  }, [trackActivitySession]);

  const handleNewChallenge = () => {
    setChallengeKey((k) => k + 1);
  };

  return (
    <ActivityLayout
      title="Space Puzzle"
      showRocketBuddy={false}
      headerExtra={
        <div className="flex items-center gap-2">
          <p className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Difficulty
          </p>
          <DifficultyPicker
            value={progress.difficulty}
            onChange={(level) => {
              setDifficulty(level);
              handleNewChallenge();
            }}
            compact
          />
        </div>
      }
    >
      <PuzzleGame
        key={`${progress.difficulty}-${challengeKey}`}
        challengeKey={challengeKey}
        onPlayAgain={handleNewChallenge}
      />
    </ActivityLayout>
  );
}
